import React, { useState, useEffect } from 'react'
import { useQuery } from 'react-apollo'
import getSearchHeroVisibility from 'graphql/getSearchHeroVisibility'
import styled from '@emotion/styled'
import clientData from 'utils/client-data'
import { NavLink, useLocation, useHistory, matchPath } from 'react-router-dom'
import { Container, Row, Col } from 'components/grid'
import { Text } from 'components/text'
import { Box } from 'components/box'
import CartBasket from './CartBasket'
import { SearchInput } from 'components/search-bar'
import * as Styled from './NavBar.styled'
import { useCurrentBreakpoint } from 'utils/hooks/useCurrentBreakpoint'
import { Message } from '@paypalcorp/worldready-react'

const elmo = clientData.experimentation

const navItems = [
  {
    to: '/',
    name: 'Home',
    exact: true,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:home::::',
    l10n: 'nav.home',
  },
  {
    to: '/all',
    name: 'AllGiftCards',
    exact: true,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:cards::::',
    l10n: 'nav.cards',
  },
  {
    to: '/bundles',
    name: 'Bundles',
    exact: true,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:bundles::::',
    l10n: 'nav.bundles',
    enabled: !!elmo.v2bundles,
  },
  {
    to: '/business',
    name: 'Local Businesses',
    exact: true,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:business::::',
    l10n: 'nav.localBusinesses',
    enabled: !!elmo.localBusinesses,
  },
  {
    to: '/gift-guides',
    name: 'Gift Guides',
    exact: false,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:guides::::',
    l10n: 'nav.giftGuides',
    enabled: !!elmo.v2guides,
  },
  {
    to: '/deals',
    name: 'Deals',
    exact: true,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:deals::::',
    l10n: 'nav.deals',
  },
  {
    to: '/categories',
    name: 'Categories',
    exact: true,
    dataPagename: 'main:consumer:gifts:home:',
    dataPagename2: 'main:consumer:gifts:categories::::',
    l10n: 'nav.categories',
    enabled: !!elmo.categoriesTab,
  },
]

const NavigationLink = styled(NavLink)`
  &:active,
  &:focus,
  &:visited {
    text-decoration: none;
  }

  p {
    color: ${(props) => props.theme.colors.secondary.greymedium};
  }

  @media (hover: hover) {
    &:hover p,
    &.active:hover {
      color: ${(props) => props.theme.colors.primary.black};
    }
  }

  &.active p {
    color: ${(props) => props.theme.colors.primary.black};
  }
`

const NavBar = () => {
  const location = useLocation()
  const history = useHistory()

  //don't show nav search bar on search page
  const isSearchPage = matchPath(location.pathname, {
    path: '/search',
    exact: false,
  })

  const [searchHeroVisible, setSearchHeroVisibility] = useState(true)
  const { data } = useQuery(getSearchHeroVisibility)
  const bkpt = useCurrentBreakpoint()

  const searchSubmit = (value) => {
    if (value.length) history.push(`/search/all?q=${encodeURIComponent(value)}`)
  }

  useEffect(() => {
    if (!data) return
    setSearchHeroVisibility(data.searchHeroInView)
  }, [data])

  return (
    <Styled.NavSticky>
      <Container>
        <Row>
          <Col xs="12">
            <Styled.NavRow
              height="4rem"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              position="relative"
              ml="-1rem"
            >
              <Styled.NavSlider
                display="flex"
                flexDirection="row"
                overflow="hidden"
                overflowX="scroll"
                pl="1rem"
              >
                {navItems
                  .filter((i) => i.enabled !== false)
                  .map((item, i) => (
                    <Box
                      key={item.name}
                      mr={i < navItems.length - 1 ? '2' : 0}
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      pr={i === navItems.length - 1 ? '60px' : '0'}
                    >
                      <NavigationLink
                        to={item.to}
                        name={item.name}
                        exact={item.exact}
                        data-pagename={item.dataPagename}
                        data-pagename2={item.dataPagename2}
                        activeClassName="active"
                      >
                        <Text textStyle="subnav" textDecoration="none">
                          <Message id={`pages/nav-bar.${item.l10n}`} />
                        </Text>
                      </NavigationLink>
                    </Box>
                  ))}
              </Styled.NavSlider>

              <Box display="flex" justify-content="flex-end">
                {bkpt > 1 && !isSearchPage && (
                  <Styled.SearchContainer show={!searchHeroVisible}>
                    <SearchInput
                      inNav={true}
                      onSubmit={searchSubmit}
                      placeholder="Search Gifts"
                    />
                  </Styled.SearchContainer>
                )}

                <NavigationLink
                  to="/cartsummary"
                  name="Cart Summary"
                  data-pagename="main:consumer:gifts:home:"
                  data-pagename2="main:consumer:gifts:home::::"
                >
                  <Styled.CartContainer
                    bg="primary.white"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    pl="1"
                  >
                    <CartBasket />
                  </Styled.CartContainer>
                </NavigationLink>
              </Box>
            </Styled.NavRow>
          </Col>
        </Row>
      </Container>
    </Styled.NavSticky>
  )
}

export default NavBar
